import { AboutData } from '@/data/types';

const aboutData: AboutData = {
  title: "Let me tell you about myself.",
  intro: [
    "I'm Robin Wichmann, a passionate software developer for over {yearsExp} years. I learned my craft in Hamburg agencies, working on projects for brands and corporations from Volkswagen to IKEA to Netflix. Today I build production AI features and my own products, including several apps in the Apple App Store.",
    "I never stand still: I'm driven by the thrill of learning new things by building them. At my core I'm a product builder, from concept all the way to the store. And I work AI-native: planned and implemented with AI agents, safeguarded by tests, mutation testing and automated pipelines.",
    "A few principles run through everything I build: privacy over data hunger, open source over lock-in, AI automated as far as possible, with a human in the loop wherever their judgment matters. Add to that a fondness for visual effects and pixel-perfect aesthetics, never at the expense of performance or accessibility."
  ],
  buttons: {
    work: "My Work",
    contact: "Contact"
  },
  skillsTitle: "My Skills & Expertise",
  skillCategories: [
    {
      name: "AI & LLM Engineering",
      skills: [
        "LLM Integration & Prompt Engineering",
        "Local LLMs (MLX, Ollama, vLLM)",
        "Agentic Workflows & Pipelines",
        "Retrieval-Augmented Generation (RAG)",
        "Embeddings & Semantic Search",
        "Streaming / SSE & Structured Output",
        "Token & Cost Optimization"
      ]
    },
    {
      name: "Frontend & Web",
      skills: [
        "TypeScript",
        "React",
        "Next.js",
        "Tailwind CSS",
        "HTML & CSS",
        "Sass/SCSS",
        "Responsive Design",
        "SEO & Pagespeed"
      ]
    },
    {
      name: "Backend & Data",
      skills: [
        "Node.js",
        "PostgreSQL",
        "Prisma",
        "REST APIs",
        "Real-time (Socket.io)",
        "Python"
      ]
    },
    {
      name: "Mobile & Native Apps",
      skills: [
        "Swift & SwiftUI",
        "Kotlin & Jetpack Compose",
        "Flutter",
        "App Store Releases (Fastlane, TestFlight)",
        "Push Infrastructure (APNs, FCM)",
        "End-to-End Encryption"
      ]
    },
    {
      name: "DevOps & Infrastructure",
      skills: [
        "Docker",
        "Coolify & Self-Hosting",
        "Git & CI",
        "S3 Object Storage",
        "Testing (Vitest, Cypress)"
      ]
    },
    {
      name: "Methods & Leadership",
      skills: [
        "Team Leadership & Tech Lead",
        "Agile Methods",
        "UX/UI Design",
        "E-Commerce",
        "Mentoring & Recruiting"
      ]
    }
  ],
  activityTitle: "Code Activity",
  activityNote: "As of July 2026",
  activityItems: [
    {
      name: "GitHub",
      description: "Personal projects & open source, 1,800+ contributions in the past year",
      image: "/assets/img/about/github_activity.webp",
      imageWidth: 1500,
      imageHeight: 345,
      accent: "green",
      link: "https://github.com/Marvbuster"
    },
    {
      name: "GitLab",
      description: "Professional development work, issues, merge requests, pushes and reviews",
      image: "/assets/img/about/gitlab_activity.webp",
      imageWidth: 1790,
      imageHeight: 390,
      accent: "blue"
    }
  ],
  careerTitle: "My Career",
  careerTimeline: [
    {
      year: "October 2025 - Today",
      title: "AI Developer",
      company: "apo.com Group GmbH",
      description: "Building production AI features end-to-end, from local inference on Apple Silicon through deterministic LLM integration to the frontend. Focused on reliability, cost control and multi-service architectures.",
      location: "Markkleeberg",
      tasks: [
        "Full-stack product development: Next.js frontend, multiple backend services, local inference and deployment in one coherent architecture",
        "Deterministic LLM programming with JSON schema validation, structured output via streaming and anti-hallucination prompt design",
        "Local LLMs on Apple Silicon via MLX (mlx_lm.server, mlx_vlm.server), including prompt cache tuning and model evaluation",
        "Multi-service architecture with a Bearer/JWT/X-API-KEY auth chain and an OpenAI-compatible proxy layer across 50+ REST endpoints",
        "Streaming/SSE-based real-time workflows: pg_notify-triggered events, ReadableStream tee for auto-persist, token streaming to the browser",
        "Agentic workflows with prioritized background workers, triggered via Server-Sent Events",
        "Token usage tracking and cost optimization with tok/s and tok/h dashboards, request-level metrics and database-wide aggregation",
        "Shop-specific prompt engineering including knowledge injection, customer context handling and reinforced language steering"
      ],
      companyType: "Online pharmacy retailer"
    },
    {
      year: "May 2024 - September 2025",
      title: "Team Lead / Tech Lead",
      company: "apo.com Group GmbH",
      description: "Led the frontend team, responsible for web technologies and e-commerce solutions. Strategic planning and coordination of development projects.",
      location: "Markkleeberg",
      tasks: [
        "Team leadership and people management including recruiting and hiring processes",
        "Mentoring and coaching of team members",
        "Technical feasibility assessments and resource planning",
        "Consulting for other departments and disciplines",
        "Strategic prototyping for new technologies and solution approaches",
        "Organizing training initiatives and knowledge transfer",
        "Technical decision-making and architecture design",
        "Coordination between product management and the development team"
      ],
      companyType: "Online pharmacy retailer"
    },
    {
      year: "March 2023 - Today",
      title: "Frontend Developer",
      company: "apo.com Group GmbH",
      description: "I'm responsible for the frontend development and optimization of various online shops and middleware implementations, with a focus on React, mobile app development and SEO.",
      location: "Markkleeberg",
      tasks: [
        "Design, development and maintenance of various online shops and middleware implementations",
        "Dynamic React components",
        "SEO and pagespeed optimizations",
        "Defining code conventions for the team regarding TypeScript and OOP",
        "Mobile and desktop app development",
        "Engineering (Webpack, Vite, Parcel…)",
        "Various prototypes (incl. Canvas, React, Vue, Storybook, StoryToDesign, Firebase Auth & RT Database)",
        "Flutter, Titanium",
        "Setting up and analyzing tags and events for GTM and GA",
        "Marketing tools and campaigns"
      ],
      companyType: "Online pharmacy retailer"
    },
    {
      year: "February 2017 - March 2023",
      title: "Senior Frontend Developer",
      company: "Plan A Internet",
      description: "As a Senior Frontend Developer I was responsible for designing and building modern websites, single page applications and server-first components, with an emphasis on performance and user experience.",
      location: "Hamburg",
      tasks: [
        "Design, development and maintenance of classic websites, SPAs and SFCs",
        "Implementation of dynamic tracking solutions",
        "SEO"
      ],
      clients: ["TV-Spielfilm", "Fit for Fun", "FRoSTA", "tesa", "Hapag Lloyd", "Gruner + Jahr"],
      companyType: "Advertising agency"
    },
    {
      year: "October 2015 - January 2017",
      title: "Freelance Web Developer",
      company: "Self-employed",
      description: "As a freelance web developer I built modern websites and applications for renowned clients, from concept to final implementation.",
      location: "Hamburg",
      tasks: [
        "Design and development of classic websites and single page applications",
        "Implementation of dynamic tracking solutions",
        "Ad creative development"
      ],
      clients: ["Volkswagen", "Netflix", "Mercedes", "tesa", "Yogi Tea"]
    },
    {
      year: "January 2012 - October 2015",
      title: "Flash Developer / Web Developer",
      company: "Grabarz & Partner Werbeagentur",
      description: "At Grabarz & Partner I was responsible for developing innovative web specials and landing pages for international markets, as well as mentoring apprentices and organizing developer events.",
      location: "Hamburg",
      tasks: [
        "Design and development of dynamic web specials and landing pages; multilingual for international markets",
        "Ad creative development",
        "Mentoring apprentices",
        "Organizing public events as a recruiting channel (\"Code in the Dark\")"
      ],
      clients: ["Volkswagen", "Volkswagen Nutzfahrzeuge", "IKEA", "Wieners & Wieners"],
      companyType: "Advertising agency"
    },
    {
      year: "September 2009 - November 2011",
      title: "Flash Developer",
      company: "deepblue networks",
      description: "At deepblue networks my focus was on developing dynamic web applications (PWA) and mobile applications with Adobe AIR (3D), as well as designing and building web applications for well-known brands. Game development for Red Bull and Knorr.",
      location: "Hamburg",
      tasks: [
        "Development of dynamic web applications (PWA) for the web as well as mobile devices (Adobe AIR)",
        "Ad creative development"
      ],
      clients: ["Unilever", "Red Bull", "Imperial Tobacco Group", "Studioline", "DPD"],
      companyType: "Advertising agency"
    },
    {
      year: "February 2009 - September 2009",
      title: "Flash Developer",
      company: "Elephant Seven (today: Digitas Pixelpark)",
      description: "After my apprenticeship I started out as a Flash Developer, creating interactive ad creatives and web specials for well-known international brands.",
      location: "Hamburg",
      clients: ["Mercedes", "Chrysler", "Storck", "Dräger", "Swarovski", "Red Bull", "Tchibo", "Lufthansa", "Palmers", "Montblanc"],
      companyType: "Advertising agency"
    },
    {
      year: "September 2006 - January 2009",
      title: "Apprentice: Media Design for Digital and Print Media (Specialization: Operating)",
      company: "Elephant Seven (today: Digitas Pixelpark)",
      description: "During my apprenticeship as a media designer with a focus on frontend development, I gained extensive experience building ad creatives and web specials for high-profile clients. Object-oriented programming in ActionScript 3.0 and Flash application development let me deepen my skills further.",
      location: "Hamburg",
      clients: ["Mercedes", "Chrysler", "Storck", "Dräger", "Swarovski", "Red Bull", "Tchibo", "Lufthansa", "Palmers", "Montblanc"],
      companyType: "Advertising agency"
    }
  ]
};

export default aboutData;
